import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { MethodBadge } from './MethodBadge';
import { CATEGORIES } from './constants';
import {
  Wrench,
  Edit,
  Trash2,
  Copy,
  Lock,
  CheckCircle2,
  XCircle,
  History
} from 'lucide-react';

export const ToolCard = ({ 
  tool, 
  onEdit, 
  onDelete, 
  onDuplicate, 
  onViewLogs,
  onToggleStatus
}) => {
  const { t } = useTranslation('workflows');
  const isActive = tool.status === 'active';
  
  return (
    <Card className={`group hover:shadow-lg transition-all duration-200 hover:-translate-y-1 border-[var(--line)] bg-[var(--surface)] ${!isActive ? 'opacity-60' : ''}`} style={{ borderRadius: 'var(--r-lg)' }}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${isActive ? 'bg-[var(--red-soft)]' : 'bg-[var(--bg-alt)]'}`}>
              <Wrench className={`h-5 w-5 ${isActive ? 'text-[var(--red)]' : 'text-[var(--ink-3)]'}`} />
            </div>
            <div>
              <p className="font-semibold text-[var(--ink)] text-[15px]">{tool.name}</p>
              <div className="flex items-center gap-2 mt-1">
                <MethodBadge method={tool.method} />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={isActive}
              onCheckedChange={(checked) => onToggleStatus(tool.id, checked ? 'active' : 'inactive')}
              data-testid={`toggle-tool-${tool.id}`}
            />
          </div>
        </div>
        
        {tool.description && (
          <p className="text-[13px] text-[var(--ink-2)] mb-3 line-clamp-2">{tool.description}</p>
        )}
        
        <div className="bg-[var(--bg-soft)] rounded-lg p-3 mb-4">
          <p className="text-[11px] text-[var(--ink-3)] font-mono truncate">
            {tool.base_url}{tool.endpoint}
          </p>
        </div>
        
        <div className="flex items-center justify-between text-[11px] text-[var(--ink-3)] mb-3">
          <span className="flex items-center gap-1">
            {tool.auth?.type !== 'none' && <Lock className="h-3 w-3" />}
            {CATEGORIES.find(c => c.code === tool.category)?.name || tool.category}
          </span>
          <div className="flex items-center gap-2">
            {tool.last_test_at && (
              <span className="flex items-center gap-1">
                {tool.last_test_result?.success ? (
                  <CheckCircle2 className="h-3 w-3 text-green-500" />
                ) : (
                  <XCircle className="h-3 w-3 text-red-500" />
                )}
                {t('tools.tested')}
              </span>
            )}
            <Badge variant={isActive ? 'default' : 'secondary'} className="text-[10px] font-semibold">
              {isActive ? t('tools.active') : t('tools.inactive')}
            </Badge>
          </div>
        </div>
        
        <div className="flex items-center justify-between pt-3 border-t border-[var(--line-soft)]">
          <span className="text-[11px] text-[var(--ink-4)]">
            {new Date(tool.created_at).toLocaleDateString()}
          </span>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="sm" onClick={() => onViewLogs(tool.id)} title={t('tools.logs')} className="h-8 w-8 p-0">
              <History className="h-4 w-4 text-[var(--ink-3)]" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDuplicate(tool.id)} title={t('tools.duplicate')} className="h-8 w-8 p-0">
              <Copy className="h-4 w-4 text-[var(--ink-3)]" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onEdit(tool)} title={t('common:edit')} className="h-8 w-8 p-0">
              <Edit className="h-4 w-4 text-[var(--ink-3)]" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(tool.id)} title={t('common:delete')} className="h-8 w-8 p-0">
              <Trash2 className="h-4 w-4 text-[var(--red)]" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ToolCard;
